import React, { Suspense, lazy, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useAuth from 'global/Auth/use';
import { RelationType } from 'api/generated/model';

import TaliaAvatar, { TALIA } from './TaliaAvatar';

//? Carregada só no primeiro clique: a biblioteca do chat não entra no pacote principal
const ChatWindow = lazy(() => import('./ChatWindow'));

//? Telas onde o botão não aparece: públicas, seleção de escritório, admin, portais de Cliente/Declarante e a
//? própria tela do Assistente (já é o chat)
const HIDDEN_PREFIXES = [
  '/login',
  '/register',
  '/forget-password',
  '/set-password',
  '/logout',
  '/confirm',
  '/lock-screen',
  '/test',
  '/error-404',
  '/error-500',
  '/maintenance',
  '/escritorios',
  '/manager',
  '/cliente',
  '/txclmt',
  '/contracts/assistant',
];

const isHiddenPath = (pathname: string) =>
  HIDDEN_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

//* Estado do assistente de um escritório: a janela fica montada (escondida) ao minimizar, para não perder a
//* conversa nem uma resposta chegando
const Launcher: React.FC<{ hidden: boolean }> = ({ hidden }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);

  //? Na tela do Assistente a janela sai (evita dois chats da mesma conversa)
  if (hidden) return null;

  const toggle = () => {
    setOpen((v) => !v);
    setOpened(true);
  };

  const expand = () => {
    setOpen(false);
    navigate(conversationId ? `/contracts/assistant?conversa=${conversationId}` : '/contracts/assistant');
  };

  return (
    <>
      {opened && (
        <div className={open ? '' : 'd-none'}>
          <Suspense fallback={null}>
            <ChatWindow
              conversationId={conversationId}
              onSelect={setConversationId}
              onMinimize={() => setOpen(false)}
              onExpand={expand}
            />
          </Suspense>
        </div>
      )}
      <div className={`ai-widget-launcher ${open ? 'is-open' : ''} ${opened ? '' : 'is-new'}`}>
        {!open && (
          <span className="ai-widget-hint" aria-hidden="true">
            Fale com a <strong>{TALIA}</strong>
          </span>
        )}
        <button
          type="button"
          className="ai-widget-button"
          onClick={toggle}
          title={open ? 'Minimizar' : TALIA}
          aria-label={open ? `Minimizar ${TALIA}` : `Abrir ${TALIA}`}
        >
          {open ? <i className="fa-solid fa-chevron-down" /> : <TaliaAvatar size="lg" />}
        </button>
      </div>
    </>
  );
};

//* Botão flutuante do assistente (spec ai-assistant-06): canto inferior direito, só para Dono/Funcionário com
//* escritório selecionado. Montado uma vez no App, então sobrevive à troca de tela
const FloatingButton: React.FC = () => {
  const { pathname } = useLocation();
  const data = useAuth((state) => state.data);

  const isOffice = data?.relationType === RelationType.Owner || data?.relationType === RelationType.Employee;
  if (!data?.user || !data.tenant || !isOffice) return null;

  //? key pelo escritório: trocar de escritório zera a conversa escolhida
  return <Launcher key={data.tenant.id} hidden={isHiddenPath(pathname)} />;
};

export default FloatingButton;
